import { type JSX, type ReactNode, useContext, useRef } from 'react';
import { persistChromeSidebarState, rightSidebarShowActionIcons } from '@gonfalon/dogfood-flags/dogfood-flags';
import { breakpoints, mediaQueries } from '@gonfalon/dom/media-queries';
import { useElementClientHeight } from '@gonfalon/dom/useElementClientHeight';
import { useMediaQuery } from '@gonfalon/dom/useMediaQuery';
import { useSlots } from '@gonfalon/react/useSlots';
import { OverflowContainer } from '@gonfalon/ui/OverflowContainer';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { useInteractOutside } from '@react-aria/interactions';
import clsx from 'clsx';
import invariant from 'tiny-invariant';

import { EnvironmentController } from '../../EnvironmentController';
import { LayoutHeader, LayoutHeaderActions } from '../../LayoutHeader';
import { useSidebarState } from '../../Sidebar';
import { getKeyboardShortcutLabel, KeyboardShortcut } from '../keyboardShortcuts';
import { useEnvironmentMode } from '../useEnvironmentMode';
import { useSidebarIsCloseByDefault } from '../useSidebarIsCloseByDefault';

import { ProjectEntityDescription } from './ProjectEntityDescription';
import { ProjectEntitySidebarContext } from './ProjectEntitySidebarContext';
import { ProjectEntityTabs } from './ProjectEntityTabs';
import {
  ResizableSidebarLayout,
  ResizeHandle,
  useResizableSidebarLayoutContext,
  useResizableSidebarLayoutState,
} from './ResizableSidebarLayout';
import { useProjectEntitySidebarContext } from './useProjectEntitySidebarContext';

import styles from './ProjectEntityLayout.module.css';

type LayoutMode = 'content' | 'tabbed' | 'configuration';

const shouldPersistChromeEntitySidebar = persistChromeSidebarState();

export function ProjectEntityLayout({ children }: { children?: ReactNode }) {
  const [{ banner, content, sidebar, tabs, headerActions }] = useSlots(children, {
    sidebar: ProjectEntitySidebar,
    content: ProjectEntityContent,
    banner: ProjectEntityBanner,
    tabs: ProjectEntityTabs,
    headerActions: ProjectEntityHeaderActions,
  });

  const closedByDefault = useSidebarIsCloseByDefault();
  const showCollapsibleSidebar = rightSidebarShowActionIcons();

  const sidebarState = useSidebarState({
    breakpoint: `${breakpoints.WIDE / 16}rem`,
    toggleShortcut: KeyboardShortcut.TOGGLE_PROJECT_ENTITY_SIDEBAR,
    preferClosedByDefault: closedByDefault,
    storageKey: shouldPersistChromeEntitySidebar ? 'project-entity-sidebar-visibility' : undefined,
    sidebarId: 'project-entity',
  });

  const layoutState = useResizableSidebarLayoutState({
    initialSidebarWidth: 300,
    minimumSidebarWidth: 300,
    maximumSidebarWidth: 480,
  });

  invariant(sidebar, 'ProjectEntityLayout must include a ProjectEntitySidebar');

  const hasEnvironmentSelector = useEnvironmentMode() !== 'all';

  let layoutMode: LayoutMode = 'content';
  if (tabs) {
    layoutMode = 'tabbed';
  } else {
    invariant(content, 'ProjectEntityLayout in content mode must include a ProjectEntityContent');
  }

  return (
    <ResizableSidebarLayout state={layoutState}>
      <ProjectEntitySidebarContext.Provider value={sidebarState}>
        <div
          className={styles.entity}
          data-has-sidebar={sidebarState.isVisible ? 'true' : undefined}
          data-dragging={layoutState.isDragging ? 'true' : undefined}
          data-collapsible-sidebar={showCollapsibleSidebar ? 'true' : undefined}
        >
          <div className={styles.workspace}>
            <LayoutHeader>{headerActions && <LayoutHeaderActions>{headerActions}</LayoutHeaderActions>}</LayoutHeader>
            {banner}
            {layoutMode === 'content' && (
              <>
                {hasEnvironmentSelector ? (
                  <div className={styles.context}>
                    <EnvironmentController />
                  </div>
                ) : null}
                <div className={styles.main}>{content}</div>
              </>
            )}
            {layoutMode === 'tabbed' && <div className={styles.main}>{tabs}</div>}
          </div>
          <div
            className={styles.aside}
            aria-hidden={sidebarState.isVisible || showCollapsibleSidebar ? undefined : 'true'}
          >
            <ResizeHandle />
            {sidebar}
          </div>
        </div>
      </ProjectEntitySidebarContext.Provider>
    </ResizableSidebarLayout>
  );
}

export function ProjectEntityHeaderActions({ children }: { children: ReactNode }) {
  return children;
}

export function ProjectEntityIntro({ children }: { children: ReactNode }) {
  const [{ title, description, code }] = useSlots(children, {
    title: ProjectEntityTitle,
    description: ProjectEntityDescription,
    code: ProjectEntityCode,
  });

  invariant(title, 'ProjectEntityIntro must include a ProjectEntityTitle');

  return (
    <div className={styles.intro}>
      {title}
      {description}
      {code}
    </div>
  );
}

export function ProjectEntityTitle({ children }: { children: ReactNode }) {
  return <h1 className={styles.title}>{children}</h1>;
}

export function ProjectEntityCode({ children }: { children: ReactNode }) {
  return <div className={styles.code}>{children}</div>;
}

export function ProjectEntitySidebar({ children }: { children: ReactNode }) {
  const sidebar = useProjectEntitySidebarContext();
  const showCollapsibleSidebar = rightSidebarShowActionIcons();
  const [{ intro, actions, footer }, rest] = useSlots(children, {
    intro: ProjectEntityIntro,
    actions: ProjectEntityActions,
    footer: ProjectEntitySidebarFooter,
  });

  const isSmall = useMediaQuery(mediaQueries.MOBILE);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { ref: containerRef, clientHeight: containerHeight } = useElementClientHeight<HTMLDivElement>();

  useInteractOutside({
    ref: sidebarRef,
    isDisabled: !sidebar.isVisible || !isSmall,
    onInteractOutside() {
      if (sidebar.isVisible) {
        sidebar.toggle({ trigger: 'click-outside' });
      }
    },
  });

  return (
    <div
      className={clsx(styles.sidebar, { [styles.isVisible]: sidebar.isVisible })}
      data-has-footer={footer ? 'true' : undefined}
      ref={sidebarRef}
    >
      {showCollapsibleSidebar ? (
        <div className={styles.actionsWrapper}>
          <div className={styles.collapseButton}>
            <ProjectEntitySidebarCollapse />
          </div>
          {actions}
        </div>
      ) : (
        actions
      )}
      {intro}
      <div ref={containerRef} className={styles.flexContainer}>
        <OverflowContainer containerHeight={containerHeight} castHeight={12}>
          <div className={styles.properties}>{rest}</div>
        </OverflowContainer>
      </div>
      {footer && <div className={styles.propertiesFooter}>{footer}</div>}
    </div>
  );
}

export function ProjectEntityActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}

export function useProjectEntityActionTooltipPlacement() {
  const sidebar = useContext(ProjectEntitySidebarContext);
  // Default to 'bottom' when not in a ProjectEntitySidebar context
  if (!sidebar) {
    return 'bottom' as const;
  }
  return sidebar.isVisible ? ('bottom' as const) : ('left' as const);
}

export function ProjectEntitySidebarCollapse() {
  const sidebar = useProjectEntitySidebarContext();
  const layout = useResizableSidebarLayoutContext();
  const showCollapsibleSidebar = rightSidebarShowActionIcons();

  if (!showCollapsibleSidebar) {
    return null;
  }

  const label = sidebar.isVisible ? 'Hide sidebar' : 'Show sidebar';
  const icon = sidebar.isVisible ? 'sidebar-right-collapse' : 'sidebar-right-expand';
  const tooltipPlacement = sidebar.isVisible ? 'bottom' : 'left';

  return (
    <TooltipTrigger>
      <IconButton
        variant="minimal"
        size="medium"
        aria-label={label}
        icon={icon}
        onPress={() => sidebar.toggle({ trigger: 'button', width: layout.width })}
        data-test-id="sidebar-toggle"
      />
      <Tooltip placement={tooltipPlacement}>
        {label} <kbd>{getKeyboardShortcutLabel(KeyboardShortcut.TOGGLE_PROJECT_ENTITY_SIDEBAR)}</kbd>
      </Tooltip>
    </TooltipTrigger>
  );
}

export function ProjectEntitySidebarFooter({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function ProjectEntityContent({ children }: { children: ReactNode }) {
  return <div className={styles.content}>{children}</div>;
}

export function ProjectEntityBanner({ children }: { children: ReactNode }) {
  return <div className={styles.banner}>{children}</div>;
}

export function EntityProperty({ children }: { children?: ReactNode }) {
  const [slots] = useSlots(children, {
    title: EntityPropertyTitle,
    description: EntityPropertyDescription,
    action: EntityPropertyAction,
  });

  return (
    <div className={styles.property}>
      {slots.title}
      {slots.description}
      {slots.action}
    </div>
  );
}

export function EntityPropertyTitle({ children }: { children?: ReactNode }) {
  return <div className={styles.propertyTitle}>{children}</div>;
}

export function EntityPropertyDescription({ children }: { children?: ReactNode }) {
  return <div className={styles.propertyDescription}>{children}</div>;
}

export function EntityPropertyAction({ children }: { children?: ReactNode }) {
  return <div className={styles.propertyAction}>{children}</div>;
}

type EntityPropertyEmptyStateProps =
  | {
      text: string;
      actionTrigger: JSX.Element;
    }
  | {
      text: string;
      actionTrigger?: never;
    }
  | {
      text?: never;
      actionTrigger: JSX.Element;
    };

export function EntityPropertyEmptyState({ text, actionTrigger }: EntityPropertyEmptyStateProps) {
  return (
    <div className={styles.propertyEmptyState}>
      {text && <TextTruncator>{text}</TextTruncator>}
      {text && actionTrigger && <span className={styles.symbol}>•</span>}
      {actionTrigger}
    </div>
  );
}
